/**
 * HARDLOCK v18.12.71-STABLE-HYDRATION-FIX
 * Archivo critico de Planeacion/Seguimiento.
 * No modificar sin snapshot, pruebas hardlock, localhost validation y revision de no regresion.
 */
export type SectionNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface SectionContract {
    type: string;
    component: string;
    canonicalKey: string;
    configKey: string;
}

export const PLANNING_SECTION_CONTRACT: Record<SectionNumber, SectionContract> = {
    1: { type: 'OBJETIVOS_AGENDA', component: 'GenericTableEditor', canonicalKey: 'sec1_data', configKey: '_config_columns_sec1' },
    2: { type: 'OBJETIVOS_PLAN', component: 'GenericTableEditor', canonicalKey: 'sec2_data', configKey: '_config_columns_sec2' },
    3: { type: 'DIAGNOSTICO', component: 'DiagnosticoFormat', canonicalKey: 'sec3_data', configKey: '_config_columns_sec3' },
    4: { type: 'ESTRUCTURA', component: 'GenericTableEditor', canonicalKey: 'sec4_data', configKey: '_config_columns_sec4' },
    5: { type: 'COMISIONES', component: 'ComisionesManager', canonicalKey: 'sec5_data', configKey: '_config_columns_sec5' },
    6: { type: 'EVENTOS', component: 'GenericTableEditor', canonicalKey: 'sec6_data', configKey: '_config_columns_sec6' },
    7: { type: 'PROYECTOS', component: 'ProyectosCardsEditor', canonicalKey: 'sec7_data', configKey: '_config_columns_sec7' },
    8: { type: 'CONVENIOS', component: 'GenericTableEditor', canonicalKey: 'convenios_data', configKey: '_config_columns_sec8' },
    9: { type: 'TEMAS', component: 'GenericTableEditor', canonicalKey: 'sec9_data', configKey: '_config_columns_sec9' },
};

const FORENSIC_MODE = false;

export const normalizeStr = (s: any) => {
    if (s === null || s === undefined) return "";
    return String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();
};

export const isMeaningfulValue = (v: any): boolean => {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    if (s === "") return false;
    
    const normalized = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    
    // Lista extendida de placeholders y valores no válidos
    const invalidValues = [
        "ESCRIBIR AQUÍ", "ESCRIBIR AQUI", "ESCRIBIR AQUÍ...", "ESCRIBIR AQUI...",
        "PÉNDIENTE", "PENDIENTE", "POR DEFINIR", "SIN ASIGNAR", "NO APLICA", "N/A", 
        "---", "--", "-", "W", "EJEMPLO", "PRUEBA", ".", "0", "N/D", "SD", "S/D",
        "NOMBRE DE VICEPRESIDENCIA", "TITULO", "ETIQUETA", "TEXTO AUXILIAR",
        "OBJETIVO ESTRATEGICO", "ACCION PRIORITARIA", "OBJETIVO", "ACCION"
    ];

    if (invalidValues.includes(normalized)) return false;
    
    // Si es solo un número (como "1.", "1)"), no es significativo para texto
    if (/^\d+[\.\)]?$/.test(s)) return false;

    return s.length > 0; // v18.12.30.1: Cualquier caracter real cuenta si no es placeholder.
};

export const isVal = (v: any): boolean => isMeaningfulValue(v);

/**
 * RESUELVE el número de sección de forma canónica (Audit v18.11.25)
 */
export const resolveCanonicalSectionNumber = (section: any): number | null => {
    if (!section) return null;
    
    // 1. Prioridad: Resolución por Nombre (Audit v18.12.18 - STOP PATCHES)
    // Usamos el nombre para determinar la sección real, ignorando números legacy si el nombre es claro.
    const name = normalizeStr(section.name || section.section_name || "");
    
    if (name.includes("OBJETIVOS DE CONTRIBUCION") || name.includes("OBJETIVOS DE LA AGENDA") || name.includes("CONTRIBUCION A LA AGENDA")) {
        return 1;
    }
    if (name.includes("OBJETIVOS DEL PLAN") || name.includes("PLAN ESTRATEGICO") || name.includes("OBJETIVOS ESTRATEGICOS") || name.includes("OBJETIVOS DEL PRESIDENTE")) {
        return 2;
    }
    if (name.includes("DIAGNOSTICO")) {
        return 3;
    }
    if (name.includes("ESTRUCTURA")) {
        return 4;
    }
    if (name.includes("COMISIONES LOCALES") || name.includes("TRABAJO CONJUNTO") || name.includes("VINCULACION") || name.includes("CREACION DE COMISIONES")) {
        return 5;
    }
    if (name.includes("EVENTO") || name.includes("ASISTENTES")) {
        return 6;
    }
    if (name.includes("PROYECTO")) {
        return 7;
    }
    if (name.includes("CONVENIO")) {
        return 8;
    }
    if (name.includes("TEMAS RELEVANTES") || name.includes("TEMAS EN LOS QUE") || name.includes("REPRESENTACION")) {
        return 9;
    }

    // 2. Fallback: Número de sección explícito en el objeto
    if (section.section_number !== null && section.section_number !== undefined) {
        const num = Number(section.section_number);
        if (!isNaN(num) && num > 0) return num;
    }

    // 3. Fallback: Número legacy
    if (section.legacy_section_number !== null && section.legacy_section_number !== undefined) {
        const num = Number(section.legacy_section_number);
        if (!isNaN(num) && num > 0) return num;
    }

    // 4. Fallback: Búsqueda en el contenido
    const content = typeof section.content === 'string' ? JSON.parse(section.content) : (section.content || {});
    if (content.section_number !== null && content.section_number !== undefined) {
        const num = Number(content.section_number);
        if (!isNaN(num) && num > 0) return num;
    }

    return null;
};

const sanitizeRow = (row: any, _sn: number): any => {
    if (!row || typeof row !== 'object') return row;
    // v18.12.71-HYDRATION-PERSISTENCE-HARDLOCK:
    // Persistencia/serialization no debe destruir columnas dinamicas.
    // Los calculos siguen filtrando campos en filledFieldCount/handlers.
    return { ...row };
};

/**
 * NORMALIZA el contenido reparando llaves legacy (Audit v18.11.25)
 */
export const normalizeSectionContent = (sn: number, rawContent: any): any => {
    if (!rawContent || typeof rawContent !== 'object') return rawContent;
    const newContent = { ...rawContent };
    const contract = PLANNING_SECTION_CONTRACT[sn as SectionNumber];
    const canonicalKey = contract ? contract.canonicalKey : `sec${sn}_data`;

    // ═══ MAPEO DE LLAVES LEGACY v18.12.18 ═══
    const legacyMap: Record<number, string[]> = {
        1: ['sec1_data', 'objetivos_data'],
        2: ['sec2_data', 'objetivos_presidente_data'],
        3: ['sec3_data', 'diagnostico_data', 'swot_sector'],
        4: ['sec4_data', 'estructura_data', 'sec3_data'],
        5: ['sec5_data', 'comisiones_data', 'sec4_data'],
        6: ['sec6_data', 'eventos_data', 'sec5_data'],
        7: ['sec7_data', 'proyectos_data', 'projects', 'sec6_data'],
        8: ['sec8_data', 'convenios_data', 'convenios', 'sec7_data'],
        9: ['sec9_data', 'temas_data', 'sec8_data']
    };

    if (!newContent[canonicalKey]) {
        const fallbacks = legacyMap[sn] || [];
        for (const key of fallbacks) {
            if (newContent[key] && (Array.isArray(newContent[key]) || typeof newContent[key] === 'object')) {
                newContent[canonicalKey] = newContent[key];
                break;
            }
        }
    }

    // SANITIZACIÓN QUIRÚRGICA v18.11.29
    if (Array.isArray(newContent[canonicalKey])) {
        newContent[canonicalKey] = newContent[canonicalKey].map((r: any) => sanitizeRow(r, sn));
    }

    if (sn === 3) {
        const s3 = newContent[canonicalKey] || {};
        if (!isMeaningfulValue(s3.antecedentes) && isMeaningfulValue(newContent.antecedentes)) s3.antecedentes = newContent.antecedentes;
        if (!s3.swot && newContent.swot_sector) s3.swot = newContent.swot_sector;
        if (!s3.swot && newContent.foda) s3.swot = newContent.foda;
        if ((!s3.temas || s3.temas.length === 0) && Array.isArray(newContent.temas_sector)) s3.temas = newContent.temas_sector;
        newContent[canonicalKey] = s3;
    }

    return newContent;
};

/**
 * LLAVES AUTORIZADAS POR SECCIÓN (Blindaje v18.11.29)
 * Evita contar "basura" de otras secciones que pueda existir en el objeto por migraciones legacy.
 */
const SECTION_FIELDS: Record<number, string[]> = {
    1: ['no_oe', 'objetivo_agenda', 'no_ap', 'accion_prioritaria', 'objetivo', 'accion'],
    2: ['no_oe', 'objetivo_estrategico', 'no_ap', 'accion_prioritaria', 'objetivo', 'accion'],
    4: ['vicepresidencia', 'nombre_responsable', 'celular', 'responsable', 'cargo'],
    6: ['mes', 'tema', 'fecha', 'evento', 'lugar'],
    7: [
        'titulo_proyecto', 'nombre', 'descripcion_estrategica', 'objetivo', 
        'avance_implementacion', 'beneficios_socios_directos', 'beneficios_centros_empresariales', 
        'inversion_presupuesto', 'aliados_estrategicos', 'descripcion_avances', 'evidencia',
        'estatus', 'progreso_porcentaje', 'metas', 'aliados'
    ],
    8: [
        'aliado', 'institucion', 'convenio', 'objetivo', 'vencimiento', 'estatus', 'aliado_institucion',
        'resultado', 'beneficio', 'nombre_aliado', 'descripcion_convenio', 'aliado_estrategico'
    ],
    9: ['tema', 't1', 't2', 't3', 't4', 'descripcion', 'objetivo']
};

const ignoredKeys = ['id', 'key', 'index', 'type', 'order', 'color', 'created_at', 'updated_at', '_id', 'rowId'];

const filledFieldCount = (row: any, sn?: number): number => {
    if (!row || typeof row !== 'object') return 0;
    const allowed = sn ? SECTION_FIELDS[sn] : null;
    
    return Object.entries(row).filter(([k, v]) => {
        if (ignoredKeys.includes(k)) return false;
        if (sn !== 8 && allowed && !allowed.includes(k)) return false; // BLINDAJE: Solo cuenta llaves permitidas (Excepción quirúrgica S8)
        return isMeaningfulValue(v);
    }).length;
};

const rowHasAtLeast = (row: any, n: number, sn?: number): boolean => {
    return filledFieldCount(row, sn) >= n;
};

export const computeSectionMetrics = (sectionNumber: number, rawContent: any, manualGoal: number = 0): { goal: number; progress: number } => {
    if (!rawContent) return { goal: manualGoal, progress: 0 };
    
    // ═══ NORMALIZACIÓN CRÍTICA v18.11.29 ═══
    const content = normalizeSectionContent(sectionNumber, rawContent);
    
    const contract = PLANNING_SECTION_CONTRACT[sectionNumber as SectionNumber];
    const canonicalKey = contract ? contract.canonicalKey : `sec${sectionNumber}_data`;

    // v18.12.26: FASE 3 - EXTRACCIÓN MULTICANAL (SHAPE-TOLERANT)
    const data = 
        Array.isArray(content[canonicalKey]) ? content[canonicalKey] : 
        (Array.isArray(content.rows) ? content.rows : 
        (Array.isArray(content.data) ? content.data : 
        (Array.isArray(content.content?.rows) ? content.content.rows : 
        (Array.isArray(content.blocks) ? content.blocks : []))));

    // FASE 2 - MAPA REAL DE CÁLCULO

    switch (sectionNumber) {
        case 1: // Objetivos Agenda
        case 2: { // Objetivos Plan
            const valid = data.filter((r, idx) => {
                const obj = r.objetivo_agenda || r.objetivo_estrategico || r.objetivo;
                const action = r.accion_prioritaria || r.accion || r.acciones_estrategicas; // v18.12.30.4: Incluye acciones_estrategicas
                const isValid = isMeaningfulValue(obj) || isMeaningfulValue(action); 
                
                
                return isValid;
            });

            const count = valid.length;
            return { goal: count, progress: count };
        }

        case 3: { // Diagnóstico Unificado (Meta 6)
            const diag = data && !Array.isArray(data) ? data : (content.sec3_data || {});
            let progress = 0;
            
            // 1. Temas (Array) - Bloque A
            if (Array.isArray(diag.temas_sector) && diag.temas_sector.some((r: any) => isMeaningfulValue(r.tema) || isMeaningfulValue(r.descripcion))) progress++;
            
            // 2-5. SWOT (Objeto) - FODA
            const swot = diag.swot_sector || {};
            if (isMeaningfulValue(swot.strengths)) progress++;
            if (isMeaningfulValue(swot.opportunities)) progress++;
            if (isMeaningfulValue(swot.weaknesses)) progress++;
            if (isMeaningfulValue(swot.threats)) progress++;
            
            // 6. Intervenciones (Array) - Bloque C
            if (Array.isArray(diag.intervenciones_previas) && diag.intervenciones_previas.some((r: any) => isMeaningfulValue(r.tema) || isMeaningfulValue(r.descripcion))) progress++;
            
            return { goal: 6, progress };
        }

        case 4: { // Estructura
            const valid = data.filter(r => {
                const name = r.nombre_responsable || r.responsable;
                const role = r.cargo || r.puesto;
                // No contar si el nombre o cargo son genéricos o vacíos
                return isMeaningfulValue(name) && !normalizeStr(name).includes("VICEPRESIDENCIA");
            });
            const count = valid.length;
            return { goal: count > 0 ? count : manualGoal, progress: count };
        }

        case 5: { // Comisiones (SOLO SI / COMENTARIO)
            const isMetaComisionValida = (v: any): boolean => {
                const norm = normalizeStr(v);
                return norm === 'MANTENER' || 
                       norm === 'ACTIVAR' || 
                       norm === 'CREAR' || 
                       norm === 'SI' || 
                       norm === 'YES' || 
                       norm === 'TRUE';
            };
            const goal = data.filter(it => isMetaComisionValida(it.meta_vinculacion || it.meta)).length;
            const progress = data.filter(it => 
                isMetaComisionValida(it.meta_vinculacion || it.meta) && 
                isMeaningfulValue(it.comentarios_vinculacion)
            ).length;
            return { goal: goal > 0 ? goal : manualGoal, progress };
        }

        case 6: { // Eventos
            const valid = data.filter(r => isMeaningfulValue(r.tema) || isMeaningfulValue(r.evento));
            const goal = valid.length;
            const progress = valid.filter(r => rowHasAtLeast(r, 2, 6)).length;
            return { goal: goal > 0 ? goal : manualGoal, progress };
        }

        case 7: { // Proyectos Estratégicos
            const valid = data.filter(r => {
                const title = r.titulo_proyecto || r.nombre;
                const progValue = Number(r.progreso_porcentaje || r.avance_implementacion || 0);
                // v18.12.30.4 BLINDAJE: En Planeación, el proyecto cuenta si tiene título O avance.
                return isMeaningfulValue(title) || progValue > 0;
            });
            return { goal: manualGoal, progress: valid.length };
        }

        case 8: { // Convenios
            const valid = data.filter(r => {
                const inst = r.institucion || r.aliado || r.aliado_institucion;
                const obj = r.objetivo || r.convenio || r.descripcion_convenio;
                return isMeaningfulValue(inst) && isMeaningfulValue(obj);
            });
            const count = valid.length;
            return { goal: count > 0 ? count : manualGoal, progress: count };
        }

        case 9: { // Temas Relevantes
            const valid = data.filter(r => isMeaningfulValue(r.tema));
            const count = valid.length;
            const progress = valid.filter(r => rowHasAtLeast(r, 2, 9)).length;
            return { goal: count > 0 ? count : manualGoal, progress };
        }

        default:
            return { goal: manualGoal, progress: 0 };
    }
};


export const serializeSection = (sectionNumber: number, content: any, planId: string, authorId: string): any[] => {
    if (!content) return [];
    const normalized = normalizeSectionContent(sectionNumber, content);
    const contract = PLANNING_SECTION_CONTRACT[sectionNumber as SectionNumber];
    const canonicalKey = contract ? contract.canonicalKey : `sec${sectionNumber}_data`;
    const data = Array.isArray(normalized[canonicalKey]) ? normalized[canonicalKey] : [];

    if (sectionNumber === 3) return [];

    return data.map((item, index) => ({
        plan_id: planId,
        section_number: sectionNumber,
        item_order: index,
        title: item.titulo_proyecto || item.nombre || item.tema || item.evento || item.objetivo || item.convenio || `Item ${index + 1}`,
        description: item.descripcion || item.descripcion_avances || item.comentarios || '',
        status: item.estatus || item.estado || 'pending',
        target_date: item.fecha || item.mes || null,
        metrics: { ...item },
        author_id: authorId
    }));
};
